import { parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson } from './rolodexEntryFields'

export enum GeckoActionType {
  BIN_VALIDATION = 'BIN_VALIDATION',
  CSCAN_CORRECTION = 'CSCAN_CORRECTION',
  CSCAN_ROTATION = 'CSCAN_ROTATION',
  DELETE_RUN = 'DELETE_RUN',
  HANDHELD_UPLOAD = 'HANDHELD_UPLOAD',
  INSPECTION_CONFIG_EDIT = 'INSPECTION_CONFIG_EDIT',
  RUN_CONFIG_EDIT = 'RUN_CONFIG_EDIT',
  PORTAL_CONFIG_DELETE = 'PORTAL_CONFIG_DELETE',
  RUN_CONFIG_RESET = 'RUN_CONFIG_RESET',
  FORECAST_PIPELINE_TRIGGER = 'FORECAST_PIPELINE_TRIGGER',
  PUBLISH = 'PUBLISH',
  MOVE_TO_QA = 'MOVE_TO_QA',
  MOVE_TO_PRE_QA = 'MOVE_TO_PRE_QA',
  ARCHIVE = 'ARCHIVE',
}

export enum GeckoActionStatus {
  QUEUED = 'QUEUED',
  PROCESSING_RUNS = 'PROCESSING_RUNS',
  AGGREGATING_RUN_DATA = 'AGGREGATING_RUN_DATA',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface GeckoUser {
  email: string
  name: string
}

export interface GeckoAction {
  action_type: GeckoActionType
  status: GeckoActionStatus
  submitted_values: Record<string, unknown>
  timestamp: Date
  values_changed: Record<string, unknown>
}

//-----------------------------------------------------------------------------

/**
 * An entry which represents actions done for an inspection.
 */
export class GeckoActionLog extends GeckoEntryBase<
  {
    user: GeckoUser
    action: GeckoAction
  },
  {
    inspection: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    const user = json.data['user'] as Record<string, unknown>
    const action = json.data['action'] as Record<string, unknown>

    super(
      json,
      {
        user: {
          email: parseString(user.email) ?? '',
          name: parseString(user.name) ?? '',
        },
        action: {
          action_type: parseString(action.action_type) as GeckoActionType,
          status: parseString(action.status) as GeckoActionStatus,
          submitted_values: action.submitted_values as Record<string, unknown>,
          timestamp: new Date(action.last_update_time as string),
          values_changed: action.values_changed as Record<string, unknown>,
        },
      },
      {
        inspection: parseString(json.tags.inspection) ?? '',
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates an organization from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoActionLog(json)
  }

  //----------------------------------------------------------------------------

  /**
   * A default sorting method for action logs (by their timestamps)
   * @param a The first action log object
   * @param b The second action log object
   */
  public static DefaultSort(a: GeckoActionLog, b: GeckoActionLog): number {
    return b.data.action.timestamp.getTime() - a.data.action.timestamp.getTime()
  }
}
